import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, History, Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DocViewer, ConsentRow, SectionTitle, KIND, fmtDT } from "@/components/legal/LegalParts";

const STATUS = {
  ACCEPTED: ["Onaylandı", "bg-emerald-50 text-emerald-800 border-emerald-200", CheckCircle2],
  OLDER_VERSION: ["Önceki sürüm onaylı", "bg-sky-50 text-sky-800 border-sky-200", History],
  PENDING: ["Onay bekleniyor", "bg-amber-50 text-amber-800 border-amber-200", AlertCircle],
  RETIRED: ["Yürürlükten kalktı", "bg-slate-50 text-slate-600 border-slate-200", Lock],
};
const TYPE = { REGISTRATION: "Kayıt sırasında", FIRST_LOGIN: "İlk giriş", RE_ACCEPT: "Yeni sürüm onayı", SETTINGS: "Ayarlar" };

export const downloadPdf = async (id) => {
  try {
    const { data, headers } = await api.get(`/legal/acceptances/${id}/pdf`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = (headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "onay.pdf";
    a.click();
    URL.revokeObjectURL(a.href);
  } catch { toast.error("PDF indirilemedi"); }
};

const HistoryRow = ({ a, onView }) => (
  <div className="flex items-center gap-3 flex-wrap py-2 text-xs border-t border-slate-100" data-testid={`legal-history-${a.id}`}>
    <span className="font-mono font-semibold text-slate-800 w-8">v{a.version}</span>
    <span className="text-slate-700 min-w-[150px]">{fmtDT(a.at)}</span>
    <span className="text-slate-500">{TYPE[a.accept_type] || a.accept_type}</span>
    <span className="text-slate-500">{a.status === "ACKNOWLEDGED" ? "Bilgilendirildi" : "Kabul edildi"}</span>
    <span className="text-slate-400 font-mono">IP {a.ip || "-"}</span>
    <span className="ml-auto flex gap-1">
      <Button size="sm" variant="ghost" className="h-7" onClick={() => onView(a.version_id)} data-testid={`legal-history-view-${a.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Metin</Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={() => downloadPdf(a.id)} data-testid={`legal-history-pdf-${a.id}`}><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
    </span>
  </div>
);

const AgreementCard = ({ item, onView, onAccept }) => {
  const [open, setOpen] = useState(false);
  const [label, cls, Icon] = STATUS[item.status];
  const last = item.history[0];
  return (
    <Card className="p-4 space-y-2" data-testid={`my-agreement-${item.doc.code || item.doc.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900">{item.doc.title}</div>
          <div className="text-xs text-slate-500">{KIND[item.doc.kind]}{item.doc.version && ` · Güncel sürüm v${item.doc.version}`}{last && ` · Son onay: v${last.version}, ${fmtDT(last.at)}`}</div>
        </div>
        <Badge variant="outline" className={cls} data-testid={`my-agreement-status-${item.doc.code || item.doc.id}`}><Icon className="w-3 h-3 mr-1" />{label}</Badge>
      </div>
      <div className="flex gap-2 flex-wrap">
        {item.doc.version_id && <Button size="sm" variant="outline" onClick={() => onView(item.doc.version_id)} data-testid={`my-agreement-view-${item.doc.code}`}><Eye className="w-3.5 h-3.5 mr-1.5" />Güncel metni aç</Button>}
        {item.doc.version_id && item.status !== "ACCEPTED" && <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => onAccept(item.doc)} data-testid={`my-agreement-accept-${item.doc.code}`}>v{item.doc.version} sürümünü onayla</Button>}
        {item.history.length > 0 && <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid={`my-agreement-history-toggle-${item.doc.code || item.doc.id}`}><History className="w-3.5 h-3.5 mr-1.5" />Onay geçmişi ({item.history.length})</Button>}
      </div>
      {open && <div>{item.history.map((a) => <HistoryRow key={a.id} a={a} onView={onView} />)}</div>}
    </Card>
  );
};

const ConsentCard = ({ item, onChange, onView }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-1">
      <ConsentRow doc={item.doc} value={item.decided ? item.granted : undefined} onChange={(v) => onChange(item.doc, v)} onOpen={() => onView(item.doc.version_id)} />
      <div className="flex items-center gap-3 px-1 text-[11px] text-slate-500">
        <span data-testid={`consent-state-${item.doc.code}`}>{item.decided ? (item.granted ? "İzin verildi" : "İzin verilmedi") : "Henüz karar verilmedi"}</span>
        {item.history.length > 0 && <button className="text-emerald-800 hover:underline" onClick={() => setOpen(!open)} data-testid={`consent-history-toggle-${item.doc.code}`}>Geçmiş ({item.history.length})</button>}
      </div>
      {open && item.history.map((e) => (
        <div key={e.id} className="flex gap-3 flex-wrap px-1 text-[11px] text-slate-600" data-testid={`consent-history-${e.id}`}>
          <span className={e.granted ? "text-emerald-700 font-semibold" : "text-slate-800 font-semibold"}>{e.granted ? "Verildi" : "Geri alındı / reddedildi"}</span>
          <span>{fmtDT(e.at)}</span><span>v{e.version}</span><span>{TYPE[e.accept_type] || e.accept_type}</span><span className="font-mono">IP {e.ip || "-"}</span>
        </div>
      ))}
    </div>
  );
};

export function MyAgreements() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [viewer, setViewer] = useState(null);
  const load = () => api.get("/legal/my").then(({ data: d }) => setData(d));
  useEffect(() => { load(); }, []);
  const view = async (vid) => { const { data: v } = await api.get(`/legal/versions/${vid}`); setViewer({ ...v, kind: v.kind }); };
  const accept = async (doc) => {
    try { await api.post("/legal/accept", { version_ids: [doc.version_id] }); toast.success("Onay kaydedildi"); load(); refresh(); }
    catch (x) { toast.error(formatApiError(x.response?.data?.detail)); load(); }
  };
  const setConsent = async (doc, granted) => {
    try { await api.post(`/legal/consents/${doc.id}`, { granted }); toast.success(granted ? "İzin verildi" : "İzin geri alındı"); load(); refresh(); }
    catch (x) { toast.error(formatApiError(x.response?.data?.detail)); }
  };
  if (!data) return <div className="text-sm text-slate-500">Yükleniyor…</div>;
  return (
    <div className="space-y-8" data-testid="my-agreements">
      <div className="space-y-3">
        <SectionTitle title="Sözleşmeler ve bildirimler" text="Onay kayıtlarınız sunucuda değiştirilemez şekilde saklanır; silinemez veya geri alınamaz. PDF olarak indirebilirsiniz." />
        {data.agreements.length === 0 && <div className="text-sm text-slate-500">Size ait sözleşme bulunmuyor.</div>}
        {data.agreements.map((it) => <AgreementCard key={it.doc.id} item={it} onView={view} onAccept={accept} />)}
      </div>
      <div className="space-y-3">
        <SectionTitle title="İsteğe bağlı izinler" text="Reklam, e-posta ve konum izinlerinizi buradan verebilir veya geri alabilirsiniz. Değişiklik geçmişi saklanır." />
        {data.consents.map((it) => <ConsentCard key={it.doc.id} item={it} onChange={setConsent} onView={view} />)}
      </div>
      <DocViewer doc={viewer} onClose={() => setViewer(null)} />
    </div>
  );
}

export default function MyAgreementsPage() {
  return (
    <div className="space-y-6 max-w-4xl" data-testid="my-agreements-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Ayarlar</div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Sözleşmelerim ve Onaylarım</h1>
      </div>
      <MyAgreements />
    </div>
  );
}
